// 多様性付きTop5選定（MMR: Maximal Marginal Relevance）

import { TweetCandidate } from './generateService';

interface DiversityConfig {
  lambda: number;           // スコア vs 多様性のバランス（0.0〜1.0）
  minTypeVariety: number;   // 最低限含めるべき型の数
  maxSameType: number;      // 同じ型の最大数
  similarityThreshold: number; // 類似度の閾値（これ以上は「被ってる」扱い）
}

const DEFAULT_CONFIG: DiversityConfig = {
  lambda: 0.7,              // スコア重視（0.7） vs 多様性（0.3）
  minTypeVariety: 3,        // 最低3種類の型を含む
  maxSameType: 2,           // 同じ型は最大2本まで
  similarityThreshold: 0.6, // 60%以上似てたら「被ってる」
};

/**
 * MMRベースの多様性付きTop5選定
 * 
 * アルゴリズム:
 * 1. スコア上位N件を候補にする（N=10〜15）
 * 2. 候補から1つずつ選ぶ際、すでに選ばれたツイートとの類似度を考慮
 * 3. 「スコアが高い」かつ「既選択と被らない」ものを優先
 * 
 * @param candidates 全候補（20本）
 * @param topK 選ぶ本数（5本推奨）
 * @param config 多様性設定
 * @returns 多様性を持たせたTop5
 */
export function selectDiverseTop(
  candidates: TweetCandidate[],
  topK: number = 5,
  config: DiversityConfig = DEFAULT_CONFIG
): TweetCandidate[] {
  if (candidates.length <= topK) {
    return candidates;
  }

  // Step1: スコア上位を候補に絞る（例: 上位15件）
  const initialPool = [...candidates]
    .sort((a, b) => b.score - a.score)
    .slice(0, Math.min(15, candidates.length));

  const selected: TweetCandidate[] = [];
  const typeCount = new Map<string, number>(); // 型の出現回数を記録

  // Step2: MMRで1つずつ選ぶ
  while (selected.length < topK && initialPool.length > 0) {
    let bestCandidate: TweetCandidate | null = null;
    let bestScore = -Infinity;
    let bestIndex = -1;

    for (let i = 0; i < initialPool.length; i++) {
      const candidate = initialPool[i];
      
      // 型の制約チェック
      const currentTypeCount = typeCount.get(candidate.templateType || 'unknown') || 0;
      if (currentTypeCount >= config.maxSameType) {
        continue; // この型はもう十分選んだ
      }

      // MMRスコア計算
      const mmrScore = calculateMMRScore(
        candidate,
        selected,
        config.lambda,
        config.similarityThreshold
      );

      if (mmrScore > bestScore) {
        bestScore = mmrScore;
        bestCandidate = candidate;
        bestIndex = i;
      }
    }

    if (bestCandidate) {
      selected.push(bestCandidate);
      
      // 型カウントを更新
      const type = bestCandidate.templateType || 'unknown';
      typeCount.set(type, (typeCount.get(type) || 0) + 1);
      
      // 選んだ候補を pool から削除
      initialPool.splice(bestIndex, 1);
    } else {
      // これ以上選べない（制約が厳しすぎる）
      break;
    }
  }

  // Step3: 型の多様性チェック（最低3種類）
  const uniqueTypes = new Set(selected.map(c => c.templateType || 'unknown'));
  if (uniqueTypes.size < config.minTypeVariety && candidates.length >= topK) {
    console.warn(`[diversityService] 型の種類が ${uniqueTypes.size} しかない（目標: ${config.minTypeVariety}）`);
    // フォールバック: スコア順で選び直す
    return candidates.slice(0, topK);
  }

  return selected;
}

/**
 * MMRスコア計算
 * 
 * MMR = λ × Relevance - (1-λ) × MaxSimilarity
 * 
 * - Relevance: 候補のスコア（0.0〜1.0）
 * - MaxSimilarity: 既選択との最大類似度（0.0〜1.0）
 * - λ: バランス係数（大きいほどスコア重視、小さいほど多様性重視）
 * 
 * @param candidate 評価対象の候補
 * @param selected すでに選ばれたツイート
 * @param lambda スコア vs 多様性のバランス
 * @param threshold 類似度の閾値（超えたらペナルティ強化）
 * @returns MMRスコア
 */
function calculateMMRScore(
  candidate: TweetCandidate,
  selected: TweetCandidate[],
  lambda: number,
  threshold: number
): number {
  const relevance = candidate.score;

  if (selected.length === 0) {
    // 最初の1本はスコアだけで選ぶ
    return relevance;
  }

  // 既選択との最大類似度を計算
  let maxSimilarity = 0;
  for (const s of selected) {
    const sim = calculateSimilarity(candidate.text, s.text);
    maxSimilarity = Math.max(maxSimilarity, sim);
  }

  // 閾値を超えたら追加ペナルティ
  let diversityPenalty = maxSimilarity;
  if (maxSimilarity > threshold) {
    diversityPenalty = maxSimilarity * 1.5; // 1.5倍のペナルティ
  }

  return lambda * relevance - (1 - lambda) * diversityPenalty;
}

/**
 * テキスト類似度計算（tri-gram Jaccard）
 * 
 * 正規化処理:
 * - 全角/半角統一
 * - 記号・絵文字削除
 * - 連続スペース削除
 * 
 * @param text1 テキスト1
 * @param text2 テキスト2
 * @returns 類似度（0.0〜1.0）
 */
function calculateSimilarity(text1: string, text2: string): number {
  // 正規化
  const normalized1 = normalizeText(text1);
  const normalized2 = normalizeText(text2);

  // tri-gram生成
  const trigrams1 = generateNGrams(normalized1, 3);
  const trigrams2 = generateNGrams(normalized2, 3);

  // Jaccard類似度
  return jaccardSimilarity(trigrams1, trigrams2);
}

/**
 * テキスト正規化
 * 
 * 1. 全角英数字 → 半角
 * 2. 絵文字削除（4バイト文字）
 * 3. 記号削除（句読点以外）
 * 4. 連続スペース → 1スペース
 * 5. 小文字化
 * 
 * @param text 元テキスト
 * @returns 正規化後
 */
function normalizeText(text: string): string {
  return text
    // 全角英数字 → 半角
    .replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0))
    // 絵文字削除（4バイト文字の簡易検出）
    .replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, '')
    // 記号削除（句読点は残す）
    .replace(/[^\p{L}\p{N}\s。、！？]/gu, '')
    // 連続スペース → 1スペース
    .replace(/\s+/g, ' ')
    // 先頭・末尾の空白削除
    .trim()
    // 小文字化
    .toLowerCase();
}

/**
 * n-gram生成
 * 
 * @param text テキスト
 * @param n n-gramのサイズ（3推奨）
 * @returns n-gramのSet
 */
function generateNGrams(text: string, n: number): Set<string> {
  const ngrams = new Set<string>();
  
  if (text.length < n) {
    ngrams.add(text);
    return ngrams;
  }

  for (let i = 0; i <= text.length - n; i++) {
    ngrams.add(text.slice(i, i + n));
  }

  return ngrams;
}

/**
 * Jaccard類似度
 * 
 * J(A, B) = |A ∩ B| / |A ∪ B|
 * 
 * @param set1 Set 1
 * @param set2 Set 2
 * @returns 類似度（0.0〜1.0）
 */
function jaccardSimilarity(set1: Set<string>, set2: Set<string>): number {
  const arr1 = Array.from(set1);
  const arr2 = Array.from(set2);
  
  const intersection = new Set(arr1.filter(x => set2.has(x)));
  const union = new Set([...arr1, ...arr2]);

  if (union.size === 0) return 0;
  return intersection.size / union.size;
}

/**
 * 型の偏り検証
 * 
 * 同じ型が多すぎる場合は警告を出す
 * 
 * @param selected 選ばれたツイート
 * @returns 型の分布統計
 */
export function analyzeTypeDistribution(selected: TweetCandidate[]): {
  uniqueTypes: number;
  distribution: Map<string, number>;
  warnings: string[];
} {
  const distribution = new Map<string, number>();
  const warnings: string[] = [];

  for (const candidate of selected) {
    const type = candidate.templateType || 'unknown';
    distribution.set(type, (distribution.get(type) || 0) + 1);
  }

  const uniqueTypes = distribution.size;

  // 警告チェック
  if (uniqueTypes < 3) {
    warnings.push(`型の種類が少ない（${uniqueTypes}種類のみ）`);
  }

  distribution.forEach((count, type) => {
    if (count > 2) {
      warnings.push(`"${type}" 型が多すぎる（${count}本）`);
    }
  });

  return { uniqueTypes, distribution, warnings };
}

/**
 * 末尾フレーズの重複チェック
 * 
 * 「わかる？」「ある？」などの末尾が被ってないかチェック
 * 
 * @param selected 選ばれたツイート
 * @returns 重複統計
 */
export function analyzeEndingDuplication(selected: TweetCandidate[]): {
  duplicates: string[];
  warnings: string[];
} {
  const endings = [
    'わかる人いる？', 'あるある？', '同士いる？', 
    'これ自分だけ？', 'どう思う？', 'ない？',
    '教えて', '知ってる？', '気になる', 'ある？',
  ];

  const duplicates: string[] = [];
  const warnings: string[] = [];
  const endingCount = new Map<string, number>();

  for (const candidate of selected) {
    for (const ending of endings) {
      if (candidate.text.includes(ending)) {
        endingCount.set(ending, (endingCount.get(ending) || 0) + 1);
      }
    }
  }

  endingCount.forEach((count, ending) => {
    if (count > 1) {
      duplicates.push(ending);
      warnings.push(`"${ending}" が ${count} 回使われている`);
    }
  });

  return { duplicates, warnings };
}

// Export config for customization
export type { DiversityConfig };
export { DEFAULT_CONFIG };
